import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { ContourLines, EvidenceTag, NetworkGraph, CornerMarks } from "@/components/Backdrop";

export const Reveal = ({
    children,
    delay = 0,
    className,
}: {
    children: ReactNode;
    delay?: number;
    className?: string;
}) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
        {children}
    </motion.div>
);

export const Eyebrow = ({ index, label, testId }: { index?: string; label: string; testId?: string }) => (
    <p
        className="flex items-center gap-3 font-tech text-[11px] font-semibold uppercase tracking-[0.3em] text-[#5B93E8]"
        data-testid={testId}
    >
        <span className="h-2 w-2 shrink-0 bg-[#A87B2F]" />
        {index ? `${index} — ${label}` : label}
    </p>
);

export const KineticWords = ({
    text,
    className,
    delay = 0,
    as: Tag = "h1",
    testId,
}: {
    text: string;
    className?: string;
    delay?: number;
    as?: "h1" | "h2" | "p";
    testId?: string;
}) => (
    <Tag className={className} data-testid={testId}>
        {text.split(" ").map((word, i) => (
            <span key={i} className="inline-block overflow-hidden pb-[0.12em] -mb-[0.12em] align-bottom">
                <motion.span
                    className="inline-block"
                    initial={{ y: "110%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.85, delay: delay + i * 0.055, ease: [0.22, 1, 0.36, 1] }}
                >
                    {word}
                </motion.span>
                {i < text.split(" ").length - 1 && <span>&nbsp;</span>}
            </span>
        ))}
    </Tag>
);

export const KineticLines = ({
    lines,
    className,
    delay = 0,
    testId,
}: {
    lines: ReactNode[];
    className?: string;
    delay?: number;
    testId?: string;
}) => (
    <h1 className={className} data-testid={testId}>
        {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: delay + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                    {line}
                </motion.span>
            </span>
        ))}
    </h1>
);

export const SectionHead = ({
    index,
    overline,
    title,
    lead,
    testId,
}: {
    index: string;
    overline: string;
    title: string;
    lead?: string;
    testId: string;
}) => (
    <Reveal className="max-w-3xl">
        <Eyebrow index={index} label={overline} testId={`${testId}-overline`} />
        <h2
            className="mt-5 font-display text-2xl font-semibold leading-[1.15] tracking-tight text-[#E8ECF4] sm:text-3xl lg:text-4xl"
            data-testid={`${testId}-title`}
        >
            {title}
        </h2>
        {lead && <p className="mt-6 text-base leading-relaxed text-[#9AA7BF]">{lead}</p>}
    </Reveal>
);

export const PageHero = ({
    overline,
    title,
    lead,
    testId,
}: {
    index?: string;
    overline: string;
    title: string;
    lead?: string;
    testId: string;
}) => (
    <header
        className="blueprint-grid relative overflow-hidden border-b border-[#243650] bg-[#0B1322]"
        data-testid={testId}
    >
        <ContourLines className="-right-32 -top-40 h-[640px] w-[640px] opacity-[0.16] lg:right-[-6rem]" />
        <NetworkGraph className="-bottom-24 left-1/2 hidden w-[900px] opacity-40 lg:block" animate={false} />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B1322] to-transparent" />
        <div className="relative mx-auto max-w-7xl px-6 pb-16 pt-36 lg:px-10 lg:pb-24 lg:pt-44">
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-wrap items-center gap-4"
            >
                <EvidenceTag label={overline} />
                <p className="font-tech text-[10px] uppercase tracking-[0.34em] text-[#8C97AC]" data-testid={`${testId}-crumb`}>
                    Infrasic Intelligence / {overline}
                </p>
            </motion.div>
            <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="mt-7 h-px w-20 origin-left bg-[#A87B2F]"
            />
            <KineticWords
                text={title}
                delay={0.25}
                testId={`${testId}-title`}
                className="mt-6 max-w-4xl font-display text-4xl font-bold leading-[1.1] tracking-tight text-[#E8ECF4] sm:text-5xl lg:text-6xl"
            />
            {lead && (
                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 max-w-2xl text-base leading-relaxed text-[#9AA7BF]"
                    data-testid={`${testId}-lead`}
                >
                    {lead}
                </motion.p>
            )}
        </div>
    </header>
);

export const PrimaryLink = ({
    to,
    children,
    testId,
}: {
    to: string;
    children: ReactNode;
    testId: string;
}) => (
    <Link
        to={to}
        data-testid={testId}
        className="group inline-flex items-center gap-3 bg-[#2E62B0] px-7 py-4 font-tech text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-[#3D74C6]"
    >
        {children}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
);

export const OutlineLink = ({
    to,
    children,
    testId,
}: {
    to: string;
    children: ReactNode;
    testId: string;
}) => (
    <Link
        to={to}
        data-testid={testId}
        className="group inline-flex items-center gap-3 border border-white/25 bg-[#0F1B2E]/60 px-7 py-4 font-tech text-[11px] font-semibold uppercase tracking-[0.22em] text-[#E8ECF4] transition-colors duration-300 hover:border-[#5B93E8] hover:text-[#5B93E8]"
    >
        {children}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </Link>
);

export const CTABand = ({
    title = "Start with the Question.",
    lead = "Every investigation begins with understanding the situation, the available evidence and the questions that need to be answered.",
    testId = "cta-band",
}: {
    title?: string;
    lead?: string;
    testId?: string;
}) => (
    <section className="blueprint-grid relative overflow-hidden border-t border-[#243650] bg-[#0F1B2E]" data-testid={testId}>
        <ContourLines className="-left-48 -bottom-56 h-[560px] w-[560px] opacity-[0.12]" rings={10} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
            <Reveal>
                <div className="certificate relative p-10 lg:p-16">
                    <CornerMarks />
                    <div className="absolute left-0 top-0 h-full w-1.5 bg-[#A87B2F]" />
                    <div className="grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
                        <div>
                            <Eyebrow label="Contact" />
                            <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-[#E8ECF4] sm:text-4xl">
                                {title}
                            </h2>
                            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#9AA7BF]">{lead}</p>
                        </div>
                        <div className="flex flex-col gap-4 sm:flex-row lg:flex-col lg:items-end">
                            <PrimaryLink to="/contact" testId={`${testId}-button-contact`}>
                                Request a Confidential Consultation
                            </PrimaryLink>
                            <OutlineLink to="/expertise" testId={`${testId}-button-expertise`}>
                                Explore Our Expertise
                            </OutlineLink>
                        </div>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

export const DuotoneImage = ({
    src,
    alt,
    className = "",
    children,
    testId,
}: {
    src: string;
    alt: string;
    className?: string;
    children?: ReactNode;
    testId?: string;
}) => (
    <div className={`relative overflow-hidden ${className}`} data-testid={testId}>
        <img src={src} alt={alt} className="absolute inset-0 h-full w-full object-cover grayscale" />
        <div className="absolute inset-0 bg-[#050B14]/78 mix-blend-multiply" />
        <div className="absolute inset-0 bg-[#1D4F9C]/25 mix-blend-overlay" />
        {children}
    </div>
);
